import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CatButton } from "blocks-nextjs/blocks-react/components/Button/Button";
import { CatDatepickerField } from "blocks-nextjs/blocks-react/components/DatepickerField/DatepickerField";
import { CatIconChevronDown } from "blocks-nextjs/blocks-react/components/Icons/ChevronDown/ChevronDown";
import { CatIconChevronUp } from "blocks-nextjs/blocks-react/components/Icons/ChevronUp/ChevronUp";
import { CatIconSearch } from "blocks-nextjs/blocks-react/components/Icons/Search/Search";
import { CatLoadingIndicator } from "blocks-nextjs/blocks-react/components/LoadingIndicator/LoadingIndicator";
import { CatPagination } from "blocks-nextjs/blocks-react/components/Pagination/Pagination";
import { CatTable } from "blocks-nextjs/blocks-react/components/Table/Table";
import { CatTableBody } from "blocks-nextjs/blocks-react/components/TableBody/TableBody";
import { CatTableCell } from "blocks-nextjs/blocks-react/components/TableCell/TableCell";
import { CatTableHeader } from "blocks-nextjs/blocks-react/components/TableHeader/TableHeader";
import { CatTableHeaderCell } from "blocks-nextjs/blocks-react/components/TableHeaderCell/TableHeaderCell";
import { CatTableRow } from "blocks-nextjs/blocks-react/components/TableRow/TableRow";
import { format, subDays } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import APIError from "../../components/APIError/APIError";
import { Variant } from "../../components/GlobalNotification/GlobalNotification";
import ChargeHeaderDetails from "../../components/HeaderDetails/ChargeHeaderDetails";
import { useChargerDetailsQuery, useChargerTransactionListQuery, useChargerTransactionsCountQuery, useChargerTransactionsQuery } from "../../services/query/eflow-charger-dashboard-api.query";
import useGlobalStore from "../../store/useGlobalStore";
import useProjectDetailsStore from "../../store/useProjectDetailsStore";
import { PropertiesForFiltering, RangeFilterType } from "../../swagger/caasService-v1";
import { formatDateWithTimezoneOffset } from "../../utils/dateFormatter";
import "./AssetPageDetail.css";
import { getAssetImageIcon, getCESTLocalTime, isLastReportedTimeStale, isTimeOnlyTimestamp } from "./ProjectDetails.helper";
const ChargeHistoryDetail = ({ chargerId, isRefreshComponent }) => {
    const [, setIsAlertsAvailable] = useState(false);
    const [, setAlertsCountForLast24hrs] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);
    const [cursor, setCursor] = useState("");
    const [skip, setSkip] = useState(0);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [searchTerm, setSearchTerm] = useState("");
    const [isDateManuallySelected, setIsDateManuallySelected] = useState(false);
    const [sortColumn, setSortColumn] = useState("transactionId");
    const [sortDirection, setSortDirection] = useState("DESC");
    // Static chargerId for testing
    const pageSizeOptions = [20, 30, 50];
    const { assetChargeActivityDetailsData, assetDetails, projectMetaData, projectName, streetAddress } = useProjectDetailsStore((state) => ({
        assetChargeActivityDetailsData: state.assetChargeActivityDetailsData,
        assetDetails: state.assetDetails,
        projectMetaData: state.projectMetaData,
        projectName: state.projectDetails?.projectName,
        streetAddress: state.projectDetails?.streetAddress
    }));
    const { setGlobalNotification } = useGlobalStore((state) => ({
        setGlobalNotification: state.setGlobalNotification
    }));
    const { assetId, timestamp = "" } = assetDetails || {};
    // Fetch charger details using the chargerId - we still fetch to get additional details like siteName
    const { data: chargerDetailsData } = useChargerDetailsQuery(chargerId || "", projectMetaData?.projectId || "", false, // Disabled: query will not run or fetch, but will return cached data if available
    undefined, false, // No refetch interval
    projectMetaData?.offset || "UTC+00:00");
    // New CaaS API returns chargerDetails (singular object), not chargers array
    const chargerDetails = chargerDetailsData?.chargerDetails;
    // Use the chargerId prop directly (already computed by parent) instead of trying to extract from cached data
    // The parent (ChargerPageDetail) already passes effectiveChargerAssetId as chargerId prop
    // Fallback order: prop > assetId (PRIMARY from search API) > chargerAssetId > assetSourceId
    const effectiveChargerAssetId = chargerId || chargerDetails?.assetId || chargerDetails?.chargerAssetId || chargerDetails?.assetSourceId;
    // If date is manually selected, use same date for from and to
    // Otherwise, use 7-day range (6 days before selectedDate to selectedDate, inclusive)
    const toDate = selectedDate;
    const fromDate = isDateManuallySelected ? selectedDate : subDays(selectedDate, 6);
    // Get timezone offset from project metadata for timezone-aware date formatting
    const timezoneOffset = useMemo(() => {
        return projectMetaData?.offset || "UTC+00:00";
    }, [projectMetaData?.offset]);
    // Memoize query parameters to prevent unnecessary re-renders and API calls
    // Format dates with timezone offset from project metadata
    const transactionQueryParams = useMemo(() => {
        const formattedFromDate = formatDateWithTimezoneOffset(fromDate, timezoneOffset);
        const formattedToDate = formatDateWithTimezoneOffset(toDate, timezoneOffset);
        const params = {
            fromTime: formattedFromDate,
            //fromTime: "2026-01-01T16:33:20.902+01:00",
            limit: pageSize.toString(),
            skip: skip.toString(),
            toTime: formattedToDate
            //toTime: "2026-01-16T16:33:20.902+01:00"
        };
        // Only include cursor if it has a value (use nextCursor as key name)
        if (cursor) {
            params.nextCursor = cursor;
        }
        return params;
    }, [cursor, fromDate, toDate, pageSize, skip, timezoneOffset]);
    //prepare request
    const chargerTransactionSearchRequest = useMemo(() => {
        const formattedFromDate = formatDateWithTimezoneOffset(fromDate, timezoneOffset);
        const formattedToDate = formatDateWithTimezoneOffset(toDate, timezoneOffset);
        const chargerTimeRange = {
            includeLeft: true,
            includeRight: false,
            left: formattedFromDate,
            right: formattedToDate
            //left: "2026-01-01T16:33:20.902+01:00",
            //right: "2026-01-16T16:33:20.902+01:00"
        };
        const chargerTimeRangeFilter = {
            propertyName: PropertiesForFiltering.TIMESTAMP,
            range: chargerTimeRange,
            type: RangeFilterType.TIME_RANGE
        };
        return { filters: [chargerTimeRangeFilter] };
    }, [fromDate, toDate, timezoneOffset]);
    const { isFetching: isChargerTransactionListFetching, isLoading: isChargerTransactionListLoading, refetch: refetchChargerTransactionList } = useChargerTransactionListQuery(chargerTransactionSearchRequest, {
        assetId: effectiveChargerAssetId || "",
        limit: 1000,
        projectId: projectMetaData?.projectId || ""
    });
    // Query parameters for count (include sort params to trigger refetch on sort change)
    const countQueryParams = useMemo(() => {
        const formattedFromDate = formatDateWithTimezoneOffset(fromDate, timezoneOffset);
        const formattedToDate = formatDateWithTimezoneOffset(toDate, timezoneOffset);
        return {
            fromTime: formattedFromDate,
            //fromTime: "2026-01-01T16:33:20.902+01:00",
            orderBy: sortDirection || "DESC",
            sortBy: sortColumn || "transactionId",
            toTime: formattedToDate
            //toTime: "2026-01-16T16:33:20.902+01:00"
        };
    }, [fromDate, toDate, sortColumn, sortDirection, timezoneOffset]);
    // Fetch transaction count
    const { data: countData } = useChargerTransactionsCountQuery(effectiveChargerAssetId || "", projectMetaData?.projectId || "", countQueryParams, !!effectiveChargerAssetId && !!projectMetaData?.projectId);
    const totalRecords = countData?.count || 0;
    // Fetch charger transactions using assetId and projectId
    const { data: chargerTransactionsData, isError: isTransactionsError, isLoading: isTransactionsLoading, refetch: refetchTransactions } = useChargerTransactionsQuery(effectiveChargerAssetId || "", projectMetaData?.projectId || "", transactionQueryParams, !!effectiveChargerAssetId && !!projectMetaData?.projectId);
    const transactions = chargerTransactionsData?.transactions || [];
    // Filter transactions based on search term across all columns
    const filteredTransactions = useMemo(() => {
        // First, apply search filtering
        const searchLower = searchTerm.toLowerCase();
        const filtered = searchTerm.trim()
            ? transactions.filter((transaction) => {
                // Search across all visible columns
                const transactionId = (transaction.transactionId || "").toLowerCase();
                const connectorId = (transaction.connectorId || "").toString().toLowerCase();
                const startTime = (transaction.startTime || "").toLowerCase();
                const endTime = (transaction.endTime || "").toLowerCase();
                const macId = (transaction.macId || "").toLowerCase();
                const initialSoc = (transaction.initialSoc?.value || "").toString().toLowerCase();
                const finalSoc = (transaction.finalSoc?.value || "").toString().toLowerCase();
                // Format SOC values with % for percentage search (e.g., "28%")
                const initialSocWithPercent = transaction.initialSoc?.value
                    ? `${transaction.initialSoc.value}%`.toLowerCase()
                    : "";
                const finalSocWithPercent = transaction.finalSoc?.value
                    ? `${transaction.finalSoc.value}%`.toLowerCase()
                    : "";
                const energyDelivered = (transaction.energyDelivered?.value || "").toString().toLowerCase();
                const stopReason = (transaction.stopReason || "").toLowerCase();
                return (transactionId.includes(searchLower) ||
                    connectorId.includes(searchLower) ||
                    startTime.includes(searchLower) ||
                    endTime.includes(searchLower) ||
                    macId.includes(searchLower) ||
                    initialSoc.includes(searchLower) ||
                    finalSoc.includes(searchLower) ||
                    initialSocWithPercent.includes(searchLower) ||
                    finalSocWithPercent.includes(searchLower) ||
                    energyDelivered.includes(searchLower) ||
                    stopReason.includes(searchLower));
            })
            : transactions;
        // Sorting is now handled server-side via API parameters
        return filtered;
    }, [transactions, searchTerm]);
    // Get nextCursor from the response metadata (at top level, not per transaction)
    const nextCursor = chargerTransactionsData?.responseMetadata?.nextCursor;
    // Handle page change
    const onPageChange = (event) => {
        const newPageSize = event.detail.pageSize;
        const newPageNumber = event.detail.pageNumber;
        // If page size changed, reset to first page
        if (newPageSize !== pageSize) {
            setPageSize(newPageSize);
            setCurrentPage(1);
            setCursor("");
            setSkip(0);
        }
        else {
            // Page number changed
            const oldPageNumber = currentPage;
            setCurrentPage(newPageNumber);
            // Calculate skip value based on page number
            const newSkip = (newPageNumber - 1) * pageSize;
            setSkip(newSkip);
            // If moving to next page and we have nextCursor, use it
            if (newPageNumber > oldPageNumber && nextCursor) {
                setCursor(nextCursor);
            }
            else if (newPageNumber === 1) {
                // Reset cursor when going back to first page
                setCursor("");
            }
        }
    };
    const onDateChange = (event) => {
        const newDate = event?.detail?.date;
        if (newDate) {
            // Convert string to Date if needed
            const dateObj = typeof newDate === "string" ? new Date(newDate) : newDate;
            setSelectedDate(dateObj);
            setIsDateManuallySelected(true); // Mark that date was manually selected
            // Reset pagination when date changes
            setCurrentPage(1);
            setCursor("");
            setSkip(0);
        }
    };
    const formatLastReportedTime = (time) => {
        if (!time)
            return "";
        // Special handling for projects where API returns time-only format (e.g., "18:00:00")
        const isTimeOnlyFormat = isTimeOnlyTimestamp(time);
        if (isTimeOnlyFormat) {
            // Time-only format: Append today's date (using browser's date)
            // Note: For projects with timezone info, timezone abbreviation should be calculated from projectMetaData
            try {
                const now = new Date();
                const month = String(now.getMonth() + 1).padStart(2, "0");
                const day = String(now.getDate()).padStart(2, "0");
                const year = now.getFullYear();
                const timeFormatted = time.substring(0, 5);
                // Use timezone abbreviation if available, otherwise default to CEST
                const timezoneAbbr = projectMetaData?.timezone ? "Local Time" : "CEST";
                return `${month}/${day}/${year}; ${timeFormatted} ${timezoneAbbr}`;
            }
            catch (error) {
                console.error("Time-only timestamp construction error:", error);
                return time.substring(0, 5);
            }
        }
        // Standard ISO timestamp formatting
        return getCESTLocalTime(time, "MM/dd/yyyy; HH:mm") + " CEST";
    };
    const msToHMS = (duration) => {
        const minutes = Math.floor((duration / (1000 * 60)) % 60);
        const hours = Math.floor((duration / (1000 * 60 * 60)) % 24);
        return parseFloat(`${hours ? hours : "0"}.${minutes ? minutes : "0"}`);
    };
    // Calculate reporting status with timezone-aware staleness check
    const isTimeOnlyFormat = isTimeOnlyTimestamp(timestamp);
    const reportingStatus = !isTimeOnlyFormat && isLastReportedTimeStale(timestamp, projectMetaData?.timeZone || projectMetaData?.timezone)
        ? "Not Reporting"
        : "Reporting";
    const legendItems = [
        { color: "#8CBAFF", label: "Idle", value: 0 },
        { color: "#1AC69E", label: "Charging", value: 0 },
        { color: "#FB5A00", label: "Discharging", value: 0 },
        { color: "#777777", label: "Not Reported", value: 0 }
    ];
    const chartValues = {
        Charging: 0,
        Discharging: 0,
        Idle: 0,
        "Not Reported": 0
    };
    const assetTimelineStatus = [];
    const assetChargeActivityDetailsDataFormatted = assetChargeActivityDetailsData.map((item) => {
        const { activityEndTimeStamp = "", activityStartTimeStamp = "" } = item;
        const todayDate = new Date().toISOString().split("T")[0] + "T";
        if (activityStartTimeStamp && activityStartTimeStamp.indexOf("T") === -1) {
            item.activityStartTimeStamp = todayDate + activityStartTimeStamp;
        }
        if (activityEndTimeStamp && activityEndTimeStamp.indexOf("T") === -1) {
            item.activityEndTimeStamp = todayDate + activityEndTimeStamp;
        }
        return item;
    });
    if (assetChargeActivityDetailsDataFormatted && assetChargeActivityDetailsDataFormatted.length > 0) {
        const assetChargeActivitySorted = assetChargeActivityDetailsDataFormatted.sort((a, b) => {
            return new Date(b.activityStartTimeStamp).getTime() - new Date(a.activityStartTimeStamp).getTime();
        });
        assetChargeActivitySorted.forEach((assetChargeActivity) => {
            const { activity = "", activityEndTimeStamp = "", activityStartTimeStamp = "", endSoc = 0, startSoc = 0 } = assetChargeActivity;
            if (assetChargeActivity.assetId === assetId) {
                const activityStatus = {
                    batteryLevel: `${Math.round(startSoc)}% -> ${Math.round(endSoc)}%`,
                    details: "",
                    time: formatLastReportedTime(activityStartTimeStamp)
                };
                if (activity === "Charging" || activity === "AC_Charging" || activity === "DC_Charging") {
                    activityStatus.details = `Charging started at ${Math.round(startSoc)}%`; // activity timeline
                    chartValues.Charging += Math.abs(new Date(activityEndTimeStamp).getTime() - new Date(activityStartTimeStamp).getTime());
                }
                else if (activity === "Idle") {
                    activityStatus.details = activity; // activity timeline
                    chartValues.Idle += Math.abs(new Date(activityEndTimeStamp).getTime() - new Date(activityStartTimeStamp).getTime());
                }
                else {
                    activityStatus.details = `Charging stopped at ${Math.round(endSoc)}%`; // activity timeline
                    chartValues.Discharging += Math.abs(new Date(activityEndTimeStamp).getTime() - new Date(activityStartTimeStamp).getTime());
                }
                const totalDiffInSoc = Math.abs(startSoc - endSoc);
                const totalDiffInMs = Math.abs(new Date(activityEndTimeStamp).getTime() - new Date(activityStartTimeStamp).getTime());
                // Only show record if total activity is greater than 2 minutes and startsoc and endsoc are not same
                if (totalDiffInSoc > 0 && totalDiffInMs > 120000) {
                    assetTimelineStatus.push(activityStatus);
                }
            }
        });
        let totalHrsRecorder = 0, notReportedIndex = 3;
        legendItems.forEach((legendItem, index) => {
            const totalSeconds = chartValues[legendItem.label];
            legendItem.value = msToHMS(totalSeconds);
            totalHrsRecorder += totalSeconds;
            if (legendItem.label === "Not Reported") {
                notReportedIndex = index;
            }
        });
        const notReportedPowerHrs = (24 - msToHMS(totalHrsRecorder)).toFixed(2);
        legendItems[notReportedIndex].value = Number(notReportedPowerHrs);
    }
    useEffect(() => {
        //Wrapping this in setTimeout without any delay helps us finding the shadow-root element
        setTimeout(() => {
            //Finding the shadow-root element to modify the styles of the pagenation dropdown
            const alertsAccordionInnerPanel = document
                .querySelector(".alerts-accordion-panel")
                ?.shadowRoot.querySelector(".cat-c-accordion-panel__body-inner");
            alertsAccordionInnerPanel?.setAttribute("style", "padding:0px;");
        });
    });
    useEffect(() => {
        if (!isRefreshComponent) {
            setIsAlertsAvailable(false);
            setAlertsCountForLast24hrs(0);
        }
    }, [isRefreshComponent]);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const last7DaysStart = new Date(today);
    last7DaysStart.setDate(today.getDate() - 6);
    const last30DaysStart = new Date(today);
    last30DaysStart.setDate(today.getDate() - 29);
    const currentWeekStart = new Date(today);
    currentWeekStart.setDate(today.getDate() - today.getDay()); // Sunday
    const currentWeekEnd = new Date(currentWeekStart);
    currentWeekEnd.setDate(currentWeekStart.getDate() + 6); // Saturday
    const previousWeekStart = new Date(currentWeekStart);
    previousWeekStart.setDate(currentWeekStart.getDate() - 7);
    const previousWeekEnd = new Date(currentWeekStart);
    previousWeekEnd.setDate(currentWeekStart.getDate() - 1);
    const handleSort = (column) => {
        // Reset pagination when sorting changes
        setCurrentPage(1);
        setCursor("");
        setSkip(0);
        if (sortColumn === column) {
            // Toggle direction: DESC ↔ ASC
            if (sortDirection === "DESC") {
                setSortDirection("ASC");
            }
            else {
                setSortDirection("DESC");
            }
        }
        else {
            // When clicking a new column, start with DESC
            setSortColumn(column);
            setSortDirection("DESC");
        }
    };
    const getSortIcon = (column) => {
        if (sortColumn === column && sortDirection) {
            return (_jsx("span", { className: "sort-icon", children: sortDirection === "ASC" ? _jsx(CatIconChevronUp, { size: "sm" }) : _jsx(CatIconChevronDown, { size: "sm" }) }));
        }
        return (_jsx("span", { className: "sort-icon sort-icon-inactive", children: _jsx(CatIconChevronDown, { size: "sm" }) }));
    };
    const handleDownloadChargerTransactionList = async () => {
        const { data: chargerTransactionList, isError } = await refetchChargerTransactionList();
        if (isError) {
            setGlobalNotification({
                message: "Error: Failed to download CSV. Please try again later.",
                showNotification: true,
                type: Variant.error
            });
            return;
        }
        // Flatten the array of arrays into a single array
        const flattenData = chargerTransactionList.flat();
        // get all transactions into a single array
        const transactionList = flattenData.reduce((accumulator, current) => {
            if (current.transactions) {
                return accumulator.concat(current.transactions);
            }
            return accumulator;
        }, []);
        downloadCSV(transactionList);
    };
    const downloadCSV = (chargerTransactionList) => {
        // Define CSV headers in desired display order
        const headers = [
            "Transaction ID",
            "Connector #",
            "Start Time",
            "End Time",
            "MAC ID",
            "Initial SOC",
            "Final SOC",
            "Energy Transferred (kWh)",
            "Stop Reason"
        ];
        // Prepare data for CSV - use array format to preserve column order
        const csvData = chargerTransactionList.map((row) => [
            row.transactionId || "-",
            row.connectorId || "-",
            row.startTime || "-",
            row.endTime || "-",
            row.macId || "-",
            row.initialSoc?.value ? `${row.initialSoc.value}%` : "-",
            row.finalSoc?.value ? `${row.finalSoc.value}%` : "-",
            row.energyDelivered?.value || "-",
            row.stopReason || "-"
        ]);
        // Create worksheet from array of arrays (preserves column order)
        const worksheet = XLSX.utils.aoa_to_sheet([headers, ...csvData]);
        // Create workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Transaction History");
        // Generate filename with current date
        const fileName = `transaction_history_${format(new Date(), "yyyy-MM-dd")}.csv`;
        // Download file
        XLSX.writeFile(workbook, fileName, { bookType: "csv" });
    };
    return (_jsxs("div", { className: "wrapper", "data-test": "asset-page-details", children: [_jsx(ChargeHeaderDetails, { activity: assetDetails?.activity, assetName: assetDetails?.assetName, capacity: assetDetails?.capacity, chargerId: chargerDetails?.assetSourceId, iconUrl: getAssetImageIcon(assetDetails), make: assetDetails?.assetMetadata?.make, name: assetDetails?.assetMetadata?.productFamily?.name, projectName: projectName || "Hansweert", reportingStatus: reportingStatus, serialNumber: assetDetails?.assetSerialNumber, siteName: chargerDetails?.siteName, streetAddress: streetAddress }), _jsx("div", { className: "flex w-full", "data-test": "asset-page-details-source", children: _jsx("div", { className: "flex flex-col w-full space-y-4", children: _jsxs("div", { children: [_jsxs("div", { className: "flex justify-between items-center mb-4 ml-4 mr-4", children: [_jsxs("div", { className: "relative flex-1 max-w-md", children: [_jsx("div", { className: "absolute inset-y-0 left-3 flex items-center pointer-events-none", children: _jsx(CatIconSearch, {}) }), _jsx("input", { className: "w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent", "data-test": "transaction-search-input", onChange: (e) => setSearchTerm(e.target.value), placeholder: "Find transactions", type: "text", value: searchTerm })] }), _jsx("div", { className: "flex items-center gap-8", children: _jsx(CatDatepickerField, { ariaDescribedBy: "daterangepicker-field-aria-1", "data-test": "transaction-date-picker", fieldId: "daterangepicker-field-1", onBlDatePickerChange: onDateChange, placeholder: "Select Date", size: "sm" }) }), _jsx(_Fragment, { children: _jsx(CatButton, { "data-test": "download-csv-button", disabled: isChargerTransactionListLoading || isChargerTransactionListFetching, onClick: handleDownloadChargerTransactionList, variant: "primary", children: _jsxs("span", { className: "inline-flex items-center gap-2", children: [(isChargerTransactionListLoading || isChargerTransactionListFetching) && (_jsx(CatLoadingIndicator, { small: true })), _jsx("span", { children: "Download CSV" })] }) }) })] }), _jsx("div", { className: "ml-4 mr-4", children: _jsxs("div", { children: [_jsx("div", { className: "transaction-table-wrapper", children: isTransactionsError ? (_jsx(APIError, { refreshHandler: () => refetchTransactions() })) : isTransactionsLoading ? (_jsx("div", { className: "flex justify-center items-center h-40", "data-test": "transactions-loading", children: _jsx(CatLoadingIndicator, {}) })) : filteredTransactions.length === 0 ? (_jsx("div", { className: "flex justify-center items-center h-40", "data-test": "transactions-empty", children: _jsx("p", { className: "text-gray-500", children: searchTerm
                                                        ? "No matching transactions found"
                                                        : "No transactions found" }) })) : (_jsxs(CatTable, { "data-test": "transactions-table", children: [_jsx(CatTableHeader, { "data-test": "transactions-table-header", children: _jsxs(CatTableRow, { className: "summary-table-row", "data-test": "transactions-header-row", children: [_jsxs(CatTableHeaderCell, { className: "sortable-header", "data-test": "transaction-id-header", onClick: () => handleSort("transactionId"), children: ["Transaction ID ", getSortIcon("transactionId")] }), _jsxs(CatTableHeaderCell, { align: "center", className: "sortable-header", "data-test": "connector-number-header", onClick: () => handleSort("connectorId"), children: ["Connector # ", getSortIcon("connectorId")] }), _jsxs(CatTableHeaderCell, { className: "sortable-header", "data-test": "start-time-header", onClick: () => handleSort("startTime"), children: ["Start Time ", getSortIcon("startTime")] }), _jsxs(CatTableHeaderCell, { className: "sortable-header", "data-test": "end-time-header", onClick: () => handleSort("endTime"), children: ["End Time ", getSortIcon("endTime")] }), _jsxs(CatTableHeaderCell, { className: "sortable-header", "data-test": "mac-id-header", onClick: () => handleSort("macId"), children: ["MAC ID ", getSortIcon("macId")] }), _jsxs(CatTableHeaderCell, { align: "center", className: "sortable-header", "data-test": "initial-soc-header", onClick: () => handleSort("initialSoc"), children: ["Initial SOC ", getSortIcon("initialSoc")] }), _jsxs(CatTableHeaderCell, { align: "center", className: "sortable-header", "data-test": "final-soc-header", onClick: () => handleSort("finalSoc"), children: ["Final SOC ", getSortIcon("finalSoc")] }), _jsxs(CatTableHeaderCell, { align: "center", className: "sortable-header", "data-test": "energy-delivered-header", onClick: () => handleSort("energyDelivered"), children: ["Energy Transferred (kWh) ", getSortIcon("energyDelivered")] }), _jsxs(CatTableHeaderCell, { className: "sortable-header", "data-test": "stop-reason-header", onClick: () => handleSort("stopReason"), children: ["Stop Reason ", getSortIcon("stopReason")] })] }) }), _jsx(CatTableBody, { "data-test": "transactions-table-body", children: filteredTransactions.map((rowCells, i) => (_jsxs(CatTableRow, { "data-test": `transaction-row-${i}`, children: [_jsx(CatTableCell, { "data-test": `transaction-id-${i}`, children: rowCells.transactionId || "-" }), _jsx(CatTableCell, { align: "center", "data-test": `connector-id-${i}`, children: rowCells.connectorId || "-" }), _jsx(CatTableCell, { "data-test": `start-time-${i}`, children: rowCells.startTime || "-" }), _jsx(CatTableCell, { "data-test": `end-time-${i}`, children: rowCells.endTime || "-" }), _jsx(CatTableCell, { "data-test": `mac-id-${i}`, children: rowCells.macId || "-" }), _jsx(CatTableCell, { align: "center", "data-test": `initial-soc-${i}`, children: rowCells.initialSoc?.value
                                                                        ? `${rowCells.initialSoc.value}%`
                                                                        : "-" }), _jsx(CatTableCell, { align: "center", "data-test": `final-soc-${i}`, children: rowCells.finalSoc?.value
                                                                        ? `${rowCells.finalSoc.value}%`
                                                                        : "-" }), _jsx(CatTableCell, { align: "center", "data-test": `energy-delivered-${i}`, children: rowCells.energyDelivered?.value || "-" }), _jsx(CatTableCell, { "data-test": `stop-reason-${i}`, children: rowCells.stopReason || "-" })] }, `row${i}${rowCells.transactionId}`))) })] })) }), _jsx("div", { className: "ml-4 mr-4", slot: "footer", children: _jsx(CatPagination, { currentItem: currentPage, "data-test": "transactions-pagination", onBlPageChange: onPageChange, pageSize: pageSize, pageSizeOptions: pageSizeOptions, totalRecords: totalRecords }) })] }) })] }) }) })] }));
};
export default ChargeHistoryDetail;
